// src/components/CashFlow/CashFlow.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import Filter_Store from "../common/Filter_store";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  LineChart, Line, ResponsiveContainer,
} from "recharts";
import "./CashFlow.css";
import MobileFiltersDrawer from "../common/MobileFiltersDrawer";
import TimeRangePicker from "../common/TimeRangePicker";
import LangSwitcher from "../common/LangSwitcher";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext";
import { useDateRange } from "../../context/DateRangeContext";
import { useFilters } from "../../context/FilterContext";
import { pivotLinesByGrowth } from "../common/growthUtils";


/* ------------------------------------------------------------------------------------------------
 * 常量 & 参数（保持与 MediaSpend 一致的初始化/同步思路；接口/渲染保留 Cashflow 自身）
 * ------------------------------------------------------------------------------------------------ */
const API_BASE = "https://boss.kfdtech.cn/api";
const AXIS_NONE = { axisLine: false, tickLine: false };
const PAGE_SIZE = 10;

/* ------------------------------------------------------------------------------------------------
 * 工具函数（与现有 Cashflow 版本一致）
 * ------------------------------------------------------------------------------------------------ */

/** 将日期对象/字符串转成 YYYY-MM */
const toYm = (d) => {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

/** 千分位 */
const fmtThousands = (n) =>
  n || n === 0 ? n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";

/** 百分比字符串 */
const pctStr = (p) => (p === 0 || p) ? `${Math.round((p <= 1 ? p * 100 : p))}%` : "";


// K/M/B 缩写（小于 1000 取整数，大于等于 1000 缩写）
const formatAbbr = (n, digits = 2) => {
  if (n == null || isNaN(n)) return "";
  const v = Number(n);
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";

  // 用来清理小数尾零
  const trim = (x) => x.toFixed(digits).replace(/\.0+$|(\.\d*[1-9])0+$/, "$1");

  // 1) 小于 1000 → 返回整数
  if (abs < 1000) return String(Math.round(v));

  // 2) ≥ 1K → 用 K/M/B 缩写
  if (abs >= 1e9) return `${sign}${trim(abs / 1e9)} B`;
  if (abs >= 1e6) return `${sign}${trim(abs / 1e6)} M`;
  if (abs >= 1e3) return `${sign}${trim(abs / 1e3)} K`;

  return String(v);
};


/** 低饱和度调色盘 */
const softPalette = [
  "#7aa2ff","#6fd0c6","#f3a683","#a29bfe","#81ecec","#ffd86b",
  "#b2bec3","#c1deff","#c7f5d9","#ffdfd3","#d1c4e9","#b3e5fc"
];
const colorForIndex = (i) => softPalette[i % softPalette.length];

/**
 * 把表格行（yoy_table/mom_table）透视成多序列折线的数据结构
 * 以净销售额 r.cur 画线；同时估一个相对稳的 y 轴 domain
 */
// function pivotLinesByNet(rows, xField) {
//   if (!Array.isArray(rows) || rows.length === 0)
//     return { data: [], keys: [], xKey: "x", domain: ["auto", "auto"] };

//   const xKeyName = xField === "year" ? "year" : "ym";
//   const allX = Array.from(new Set(rows.map((r) => String(r[xKeyName])))).sort();
//   const brands = Array.from(new Set(rows.map((r) => r.store))).sort();

//   const map = new Map();
//   for (const x of allX) map.set(x, { x });

//   rows.forEach((r) => {
//     const x = String(r[xKeyName]);
//     const target = map.get(x);
//     const g = (r.growth === 0 || r.growth) ? Number(r.growth) : null;

//   // 极端值过滤（避免爆炸图）
//   target[r.store] = (g == null || !isFinite(g) || Math.abs(g) > 300) ? null : g;

//   });

//   const data = Array.from(map.values());

//   // 估 5%~95% 分位范围，且必须包含 0
//   const vals = [];
//   data.forEach((d) => brands.forEach((b) => {
//     if (d[b] || d[b] === 0) vals.push(d[b]);
//   }));

//   let domain = ["auto", "auto"];
//   if (vals.length > 2) {
//     const sorted = vals.slice().sort((a, b) => a - b);
//     const q = (p) => {
//       const i = (sorted.length - 1) * p;
//       const lo = Math.floor(i), hi = Math.ceil(i);
//       if (lo === hi) return sorted[lo];
//       return sorted[lo] + (sorted[hi] - sorted[lo]) * (i - lo);
//     };
//     const p5 = q(0.05), p95 = q(0.95);
//     domain = [Math.min(0, p5), Math.max(0, p95)];
//   }

//   return { data, keys: brands, xKey: "x", domain };
// }

/* ------------------------------------------------------------------------------------------------
 * YoY / MoM 卡片（保持你的结构与样式）
 * ------------------------------------------------------------------------------------------------ */
const TableCard = ({ title, mode, onToggle, schema, rows = [], brandColors = {} }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [page, setPage] = useState(1);
  const PAGE = 5;
  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  const start = (page - 1) * PAGE;
  const pageRows = rows.slice(start, start + PAGE);
  const pad = Math.max(0, PAGE - pageRows.length);
  const paddedRows = [...pageRows, ...Array.from({ length: pad }, () => null)];

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(pages, p + 1));

  return (
    <div className="growth-card">
      <div className="card-tools">
        <h4>{title}</h4>
        <button
          type="button"
          className="btn-toggle top-right"
          onClick={() => onToggle(mode === "chart" ? "table" : "chart")}
        >
          {mode === "chart" ? t("table") : t("chart")}
        </button>
      </div>

      {mode === "chart" ? (
        <div className="growth-body">
          {schema.data?.length ? (
            <div className="line-wrap">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={schema.data}
                  margin={{
                    top: 8,
                    right: 16,
                    left: window.innerWidth < 768 ? 16 : 64, // ✅ 手机端缩小左边距
                    bottom: 0,
                  }}
                >
                  <XAxis dataKey={schema.xKey} {...AXIS_NONE} />
                  <YAxis
                    label={{
                      value: schema.kind === "yoy" ? t("YoY Growth %") : t("MtoM Growth %"),
                      angle: -90,
                      position: "insideLeft",
                      offset: 10,
                      style: { fill: "#666", fontSize: 12 }
                    }}
                    {...AXIS_NONE}
                    width={window.innerWidth < 768 ? 40 : 80} // ✅ 手机端缩小Y轴宽度
                    tickMargin={4}
                    tickFormatter={(v) => `${v.toFixed(0)}%`}
                    domain={schema.domain}
                  />
                  <Tooltip formatter={(v) => (v == null ? "N/A" : `${Number(v).toFixed(1)}%`)} />
                  {schema.keys.map((b, i) => (
                    <Line
                      key={b}
                      type="monotone"
                      dataKey={b}
                      stroke={brandColors[b] || colorForIndex(i)}
                      dot={false}
                      strokeWidth={2}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="no-data">No data</div>
          )}
        </div>
      ) : (
        <div className="table-wrap">
          {rows.length === 0 ? (
            <div className="no-data">No data</div>
          ) : (
            <>
              <table className="mini-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>{t("Stores")}</th>
                    <th style={{ textAlign: "right" }}>
                      {schema.kind === "yoy" ? t("year") : t("yearMonth")}
                    </th>
                    <th style={{ textAlign: "right" }}>{t("NetCashFlow")}</th>
                    <th style={{ textAlign: "right" }}>
                      {schema.kind === "yoy" ? t('NetCashFlow_lastyear') : t('NetCashFlow_lastmonth')}
                    </th>
                    <th style={{ textAlign: "right" }}>
                      {schema.kind === "yoy" ? t('YoY Growth %') : t('MtoM Growth %')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paddedRows.map((r, i) =>
                    r ? (
                      <tr key={`${r.store}-${schema.kind === "yoy" ? r.year : r.ym}-${i}`}>
                        <td>{r.store}</td>
                        <td style={{ textAlign: "right" }}>
                          {schema.kind === "yoy" ? r.year : r.ym}
                        </td>
                        <td style={{ textAlign: "right" }}>{formatAbbr(r.cur, 2)}</td>
                        <td style={{ textAlign: "right" }}>{formatAbbr(r.prev, 2)}</td>
                        <td style={{ textAlign: "right" }}>
                          {r.growth == null ? "N/A" : `${r.growth.toFixed(1)}%`}
                        </td>
                      </tr>
                    ) : (
                      <tr key={`pad-${i}`}>
                        <td>&nbsp;</td>
                        <td style={{ textAlign: "right" }}>&nbsp;</td>
                        <td style={{ textAlign: "right" }}>&nbsp;</td>
                        <td style={{ textAlign: "right" }}>&nbsp;</td>
                        <td style={{ textAlign: "right" }}>&nbsp;</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>

              <div className="mini-pager">
                <button className="pager-btn" disabled={page === 1} onClick={goPrev}>
                  Prev
                </button>
                <span style={{ margin: "0 8px" }}>
                  {page}/{pages}
                </span>
                <button className="pager-btn" disabled={page === pages} onClick={goNext}>
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------------------------------------
 * 主页面：Cashflow（筛选与同步逻辑 —— 完全复刻 MediaSpend.js）
 * ------------------------------------------------------------------------------------------------ */
export default function CashFlow() {
  const { authFetch } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  /* 日期范围（保持组件交互一致） */
  const { dateRange, setDateRange } = useDateRange();

  /* 全局 Filter Context */
  const { filters, setFilters } = useFilters();

  /* 候选项与本地 UI 状态（与 MediaSpend 相同） */
  const [filterOpts, setFilterOpts] = useState({ stores: [], platforms: [], brand2plats: {} });
  const filterLoading = filterOpts.stores.length === 0 && filterOpts.platforms.length === 0;

  const platTouchedRef = useRef(false);   // 用户是否主动改过平台
  const initializedRef = useRef(false);   // 避免重复初始化
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* 数据容器（沿用 CashFlow 的结构与字段） */
  const [cards, setCards] = useState({ gmv: 0, netSales: 0, refund: 0, mediaCost: 0 });
  const [bar, setBar] = useState([]);
  const [yoyTable, setYoyTable] = useState([]);
  const [momTable, setMomTable] = useState([]);
  const [rows, setRows] = useState([]);
  const [yoyMode, setYoyMode] = useState("chart");
  const [momMode, setMomMode] = useState("chart");
  const [detailsPage, setDetailsPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");



  /* ---------- 与 MediaSpend 一致：初始化筛选项（接口 -> Context/LS 的三段式） ---------- */
  /* ---------- Stores 风格：初始化筛选项 ---------- */
useEffect(() => {
  let abort = false;

  (async () => {
    try {
      const r = await authFetch(`${API_BASE}/filters`);
      const j = await r.json();
      if (!r.ok)
        throw new Error(j?.detail || j?.error || `HTTP ${r.status}`);
      if (abort) return;

      const stores = j.stores || [];
      const platforms = j.platforms || [];
      const brand2plats = j.brand2plats || {};
      setFilterOpts({ stores, platforms, brand2plats });

      if (!initializedRef.current) {
        initializedRef.current = true;

        const hasContextFilters =
          (filters?.stores?.length ?? 0) > 0 ||
          (filters?.platforms?.length ?? 0) > 0;

        // Context 没有过滤器 → 默认全选
        if (!hasContextFilters) {
          const all = {
            stores: stores.slice(),
            platforms: platforms.slice(),
          };
          setFilters(all);
          sessionStorage.setItem("filters", JSON.stringify(all));
        }
      }
    } catch (e) {
      if (!abort) setErr(String(e?.message || e));
    }
  })();

  return () => {
    abort = true;
  };
}, []);


  /* ---------- 与 Stores.js 一致：allowedPlatforms ---------- */
const allowedPlatforms = useMemo(() => {
  const sel = filters.stores;
  if (!sel?.length) return filterOpts.platforms;

  const acc = new Set();
  sel.forEach((b) =>
    (filterOpts.brand2plats[b] || []).forEach((p) => acc.add(p))
  );
  return Array.from(acc).sort((a, b) => a.localeCompare(b));
}, [filters.stores, filterOpts]);


  /* ---------- 与 MediaSpend 一致：平台自动收敛/一次性补全 ---------- */
  useEffect(() => {
    if (filterLoading) return; // 候选未到位不处理
    const cur = filters?.platforms || [];
    const next = cur.filter((p) => allowedPlatforms.includes(p));
    if (next.length !== cur.length) {
      setFilters((f) => ({ ...f, platforms: next }));
      try {
        const saved = JSON.parse(localStorage.getItem("filters") || "{}");
        localStorage.setItem("filters", JSON.stringify({ ...saved, platforms: next }));
      } catch {}
      return;
    }
    const bootstrapped = localStorage.getItem("filters_bootstrapped") === "1";
    if (!platTouchedRef.current && cur.length === 0 && allowedPlatforms.length && !bootstrapped) {
      const autoFill = allowedPlatforms.slice();
      setFilters((f) => ({ ...f, platforms: autoFill }));
      try {
        const saved = JSON.parse(localStorage.getItem("filters") || "{}");
        localStorage.setItem("filters", JSON.stringify({ ...saved, platforms: autoFill }));
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedPlatforms, filterLoading]);

  /* ---------- Stores 风格：筛选改变 ---------- */
const onFiltersChange = (next) => {
  if (filterLoading) return;
  setFilters(next);
  sessionStorage.setItem("filters", JSON.stringify(next));
};


  /* ---------- 与 MediaSpend 一致：构造请求参数（必须等筛选项加载完成，且筛选非空） ---------- */
  const payload = useMemo(() => {
    const start = toYm(dateRange?.startDate);
    const end = toYm(dateRange?.endDate);
    const brands = Array.isArray(filters?.stores) ? filters.stores : [];
    const plats = Array.isArray(filters?.platforms) ? filters.platforms : [];
    if (!start || !end) return null;
    if (brands.length === 0 || plats.length === 0) return null;
    return {
      YearMonth_List: [start, end],
      Brand_List: brands,
      Platform_List: plats,
      allow_current_month: dateRange?.mode === "custom",
    };
  }, [dateRange, filters]);

  /* ---------- 与 MediaSpend 一致：拉取数据（filterLoading 或 payload 空则不请求） ---------- */
  useEffect(() => {
    let abort = false;
    (async () => {
      if (filterLoading || !payload) {
        setLoading(false);
        setErr("");
        setCards({ gmv: 0, netSales: 0, refund: 0, mediaCost: 0 });
        setBar([]); setYoyTable([]); setMomTable([]); setRows([]);
        return;
      }
      try {
        setLoading(true);
        setErr("");
        const r = await authFetch(`${API_BASE}/cashflow`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j?.detail || j?.error || `HTTP ${r.status}`);
        if (abort) return;

        setCards(j?.cards || { gmv: 0, netSales: 0, refund: 0, mediaCost: 0 });
        setBar(j?.bar || []);
        setYoyTable(j?.yoy_table || []);
        setMomTable(j?.mom_table || []);
        setRows(j?.details || []);
        setDetailsPage(1);
      } catch (e) {
        if (!abort) setErr(String(e?.message || e));
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [payload, filterLoading, authFetch]);

  /* ---------- 折线图数据 & 颜色映射（保持 Cashflow 现有逻辑） ---------- */
  const yoyLines = useMemo(() => pivotLinesByGrowth(yoyTable, "year"), [yoyTable]);
  const momLines = useMemo(() => pivotLinesByGrowth(momTable, "ym"), [momTable]);


  const brandColors = useMemo(() => {
    const names = Array.from(new Set([...(yoyLines.keys || []), ...(momLines.keys || [])])).sort();
    const map = {};
    names.forEach((n, idx) => { map[n] = colorForIndex(idx); });
    return map;
  }, [yoyLines.keys, momLines.keys]);

  /* ---------- Details 分页（保持 Cashflow 现有逻辑） ---------- */
  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIndex = (detailsPage - 1) * PAGE_SIZE;
  const pageRows = rows.slice(startIndex, startIndex + PAGE_SIZE);
  const padCount = Math.max(0, PAGE_SIZE - pageRows.length);
  const paddedRows = [...pageRows, ...Array.from({ length: padCount }, () => null)];

  /* ---------- 渲染 ---------- */
  return (
    <>
      <div className="cashflow-layout">
        <div className="main-panel">
          {/* 顶栏 */}
          <div className="page-topbar">
            <h1 className="site-title">{t("title")}</h1>
            <div className="only-mobile"><LangSwitcher /></div>
          </div>

          

          {/* 页内标题 + 时间/移动筛选 */}
          <div className="overview-header" style={{ marginTop: 6 }}>
            <div className="page-subtitle">{t('Cash Flow')}</div>
            <div className="header-right">
              <button type="button" className="filter-button-mobile" onClick={() => setDrawerOpen(true)}>筛选</button>
              <TimeRangePicker value={dateRange} onChange={setDateRange} />
            </div>
          </div>

          {/* KPI */}
          <div className="stat-cards">
            {[
              { title: t('CashReceived'),       value: cards.gmv },
              { title: t('NetCashFlow'),  value: cards.netSales },
              { title: t('RefundPaid'),    value: cards.refund },
              { title: t('mediaCost'), value: cards.mediaCost },
            ].map((it, i) => (
              <div key={it.title} className={`card ${i % 2 === 0 ? "bg-purple" : "bg-blue"}`}>
                <div className="card-title">{it.title}</div>
                <div className="card-value">{formatAbbr(it.value, 2)}</div>
              </div>
            ))}
          </div>

          {/* 柱状图 */}
          <div className="chart-section">
            <div className="chart-header">
              <h3>{t("StoreNetCashFlow")}</h3>
              <div className="chart-legend">
                <span className="divider" />
                <span className="legend-item">
                  <span className="dot blue" /> {t("NetCashFlow")}
                </span>
                <span className="legend-item">
                  <span className="dot gray" /> {t("RefundPaid")}
                </span>
              </div>
            </div>
            <div className="chart-wrap">
              {bar.length === 0 ? (
                <div className="no-data">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bar} margin={{ top: 6, right: 16, left: -10, bottom: 0 }}>
                    <XAxis dataKey="date" {...AXIS_NONE} />
                    <YAxis {...AXIS_NONE} tickFormatter={(v) => formatAbbr(v, 1)} />
                    <Tooltip formatter={(v) => formatAbbr(v, 2)} />
                    <Bar
                      dataKey="netSales" name={t('NetCashFlow')} stackId="a" fill="#8cb4f0"
                      barSize={20} radius={[10, 10, 10, 10]}
                      isAnimationActive={false} animationDuration={0}
                    />
                    <Bar
                      dataKey="refund" name={t('RefundPaid')} stackId="a" fill="#b3b3b3"
                      barSize={20} radius={[10, 10, 10, 10]}
                      isAnimationActive={false} animationDuration={0}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* YoY / MoM */}
          <div className="growth-section">
            <TableCard
              title={t("yoyGrowth")}
              mode={yoyMode}
              onToggle={setYoyMode}
              schema={{ kind: "yoy", ...yoyLines }}
              rows={yoyTable}
              brandColors={brandColors}
            />
            <TableCard
              title={t("momGrowth")}
              mode={momMode}
              onToggle={setMomMode}
              schema={{ kind: "mom", ...momLines }}
              rows={momTable}
              brandColors={brandColors}
            />
          </div>

          {/* Details */}
          <div className="table-card">
            <h3>{t("Details")}</h3>
            <p className="table-note">
              {t('description')}
              {/* Note: This table is affected only by the <b>Time Range</b> and <b>Stores</b> filters,
              not by the <b>Platform</b> filter, as many expense items are provided on a monthly basis. */}
            </p>
            <div className="table-scroll">
              <table className="data-table details-table">
                <thead>
                  <tr>
                    <th className="sticky-col">{t("storeName")}</th>
                    <th className="ta-right">{t("CashReceived")}</th>
                    <th className="ta-right">{t("NetCashFlow")}</th>
                    <th className="ta-right">{t("KFDCashShare")}</th>
                    <th colSpan={7} className="ta-center group">{t("Operations Expenses")}</th>
                    <th className="ta-right">{t("Other Income")}</th>
                    <th className="ta-right">{t("Net Profit")}</th>
                  </tr>
                  <tr>
                    <th className="spacer sticky-col"></th>
                    <th className="spacer"></th>
                    <th className="spacer"></th>
                    <th className="spacer"></th>
                    <th className="ta-right">{t('VAT')}</th>
                    <th className="ta-right">{t("Platform Costs")}</th>
                    <th className="ta-right th-wrap">{t("Warehousing")}<br/>/ Logistics</th>
                    <th className="ta-right">{t("Marketing")}</th>
                    <th className="ta-right">{t("Personnel")}</th>
                    <th className="ta-right">{t("Other")}</th>
                    <th className="ta-right">{t("Total")}</th>
                    <th className="spacer"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td className="ta-center no-data-row" colSpan={1}>No data</td></tr>
                  ) : (
                    paddedRows.map((r, idx) => (
                      <tr key={idx}>
                        <td className="sticky-col">{r ? r.store : "\u00A0"}</td>

                        <td className="ta-right">
                          <div className="cell-val">{r ? fmtThousands(r.gross) : "\u00A0"}</div>
                          <div className="cell-sub">{r && (r.grossPct === 0 || r.grossPct) ? pctStr(r.grossPct) : "\u00A0"}</div>
                        </td>
                        <td className="ta-right">
                          <div className="cell-val">{r ? fmtThousands(r.net) : "\u00A0"}</div>
                          <div className="cell-sub">{r ? pctStr(r.netPct) : "\u00A0"}</div>
                        </td>
                        <td className="ta-right">
                          <div className="cell-val">{r ? fmtThousands(r.kfd) : "\u00A0"}</div>
                          <div className="cell-sub">{r ? pctStr(r.kfdPct) : "\u00A0"}</div>
                        </td>

                        <td className="ta-right">
                          <div className="cell-val">{r ? fmtThousands(r.vat) : "\u00A0"}</div>
                          <div className="cell-sub">{r ? pctStr(r.vatPct) : "\u00A0"}</div>
                        </td>
                        <td className="ta-right">
                          <div className="cell-val">{r ? fmtThousands(r.platform) : "\u00A0"}</div>
                          <div className="cell-sub">{r ? pctStr(r.platformPct) : "\u00A0"}</div>
                        </td>
                        <td className="ta-right">
                          <div className="cell-val">{r ? fmtThousands(r.warehousing) : "\u00A0"}</div>
                          <div className="cell-sub">{r ? pctStr(r.warehousingPct) : "\u00A0"}</div>
                        </td>
                        <td className="ta-right">
                          <div className="cell-val">{r ? fmtThousands(r.marketing) : "\u00A0"}</div>
                          <div className="cell-sub">{r ? pctStr(r.marketingPct) : "\u00A0"}</div>
                        </td>
                        <td className="ta-right">
                          <div className="cell-val">{r ? fmtThousands(r.personnel) : "\u00A0"}</div>
                          <div className="cell-sub">{r ? pctStr(r.personnelPct) : "\u00A0"}</div>
                        </td>
                        <td className="ta-right">
                          <div className="cell-val">{r ? fmtThousands(r.other) : "\u00A0"}</div>
                          <div className="cell-sub">{r ? pctStr(r.otherPct) : "\u00A0"}</div>
                        </td>
                        <td className="ta-right">
                          <div className="cell-val">{r ? fmtThousands(r.total) : "\u00A0"}</div>
                          <div className="cell-sub">{r ? pctStr(r.totalPct) : "\u00A0"}</div>
                        </td>
                        <td className="ta-right">
                        <div className="cell-val">{r ? fmtThousands(r.other_income) : "\u00A0"}</div>
                        <div className="cell-sub">{"\u00A0"}</div>
                      </td>
                        <td className="ta-right">
                          <div className="cell-val">{r ? fmtThousands(r.netProfit) : "\u00A0"}</div>
                          <div className="cell-sub">{"\u00A0"}</div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="table-pager">
              <button className="pager-btn" disabled={detailsPage === 1} onClick={() => setDetailsPage(1)}>«</button>
              <button className="pager-btn" disabled={detailsPage === 1} onClick={() => setDetailsPage((p) => Math.max(1, p - 1))}>Prev</button>
              <span className="pager-info">
                {total ? `${startIndex + 1}-${Math.min(startIndex + PAGE_SIZE, total)} of ${total}` : "0-0 of 0"}&nbsp;|&nbsp; Page {detailsPage}/{pages}
              </span>
              <button className="pager-btn" disabled={detailsPage === pages} onClick={() => setDetailsPage((p) => Math.min(pages, p + 1))}>Next</button>
              <button className="pager-btn" disabled={detailsPage === pages} onClick={() => setDetailsPage(pages)}>»</button>
            </div>
          </div>

          {loading && (
            <div className="loading-overlay">
              <div className="loading-card">
                <div className="spinner" />
                <div className="loading-text">Loading…</div>
              </div>
            </div>
          )}
          {err && !loading && (
            <div className="loading-overlay">
              <div className="loading-card">
                <div className="loading-text">Error: {err}</div>
              </div>
            </div>
          )}

          <div className="bottom-spacer" aria-hidden="true" />
        </div>

        {/* 右栏：语言切换 + 筛选 */}
        <div className="filters-sidebar">
          <div className="filters-head"><LangSwitcher /></div>
          <Filter_Store
            loading={filterLoading}
            stores={filterOpts.stores}
            platforms={allowedPlatforms}
            value={filters}
            onChange={onFiltersChange}
          />
        </div>
      </div>

      {/* 移动端抽屉筛选 */}
      <MobileFiltersDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Filter_Store
          loading={filterLoading}
          stores={filterOpts.stores}
          platforms={allowedPlatforms}
          value={filters}
          onChange={onFiltersChange}
        />
      </MobileFiltersDrawer>
    </>
  );
}
